import React, { useState, useEffect } from 'react';
import api from '../services/apiServices';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Image as ImageIcon, File, Search, X } from 'lucide-react';

// FIX 1: Change value: '' to value: 'ROOT' to satisfy Shadcn/Radix
const FOLDERS = [
  { label: 'SEO Hero Images', value: 'public/seo/hero-images/' },
  { label: 'Content Assets', value: 'content/assets/' },
  { label: 'All SEO', value: 'public/seo/' },
  { label: 'Root', value: 'ROOT' }, 
];

const MediaLibraryModal = ({ open, onOpenChange, onSelect }) => {
  const CDN_BASE = process.env.REACT_APP_S3_ASSETS_FETCH_URL || "https://cdn.selyst.com";
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [prefix, setPrefix] = useState('seo/hero-images/');
  const [nextToken, setNextToken] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const getPreviewUrl = (fileKey) => {
      // Join CDN + Key safely
      return `${CDN_BASE.replace(/\/$/, '')}/${fileKey.replace(/^\//, '')}`;
  };

  const fetchFiles = async (token = null) => {
    setLoading(true);
    try {
      // FIX 2: Swap 'ROOT' back to empty string for the API call
      const apiPrefix = prefix === 'ROOT' ? '' : prefix;

      const params = { prefix: apiPrefix, continuation_token: token };
      if (searchQuery) params.search = searchQuery;

      const res = await api.get('/admin/upload/files/list', { params });

      if (token) {
        setFiles(prev => [...prev, ...res.data.files]);
      } else {
        setFiles(res.data.files);
      }
      setNextToken(res.data.next_token);
    } catch (err) {
      console.error("Failed to load media", err);
    } finally {
      setLoading(false);
    }
  };

  // Reload when folder, search query, or modal open state changes
  useEffect(() => {
    if (open) {
      setFiles([]);
      setNextToken(null);
      fetchFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefix, searchQuery]);

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const handleSelect = (file) => {
    onSelect(file.url); 
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Media Library</span>
            <div className="w-[200px] mr-6">
                <Select value={prefix} onValueChange={(v) => { setSearchInput(''); setSearchQuery(''); setPrefix(v); }}>
                    <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        {FOLDERS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>
          </DialogTitle>
          <form onSubmit={handleSearch} className="flex items-center gap-2 pt-1">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by filename…"
                className="pl-8 h-8 text-sm"
              />
              {searchQuery && (
                <button type="button" onClick={clearSearch} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button type="submit" size="sm" variant="outline" className="h-8 px-3">Search</Button>
          </form>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-1 border rounded-md bg-gray-50">
          {files.length === 0 && !loading && (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <ImageIcon className="w-12 h-12 mb-2 opacity-20"/>
                <p>No files found in this folder</p>
            </div>
          )}

          <div className="grid grid-cols-4 sm:grid-cols-5 gap-4 p-2">
            {files.map((file) => (
              <div 
                key={file.key} 
                onClick={() => handleSelect(file)}
                className="group relative aspect-square border rounded-lg overflow-hidden bg-white cursor-pointer hover:ring-2 ring-blue-500 transition-all shadow-sm"
              >
                {file.key.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                        // FIX: Use the computed CDN URL for preview
                        <img 
                        src={getPreviewUrl(file.key)} 
                        alt={file.key} 
                        className="w-full h-full object-cover" 
                        loading="lazy" 
                        />
                    ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
                        <File className="w-8 h-8" />
                    </div>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-black/60 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-[10px] text-white truncate font-mono">{file.key.split('/').pop()}</p>
                </div>
              </div>
            ))}
          </div>
          
          {loading && <div className="py-4 flex justify-center"><Loader2 className="animate-spin text-blue-500"/></div>}
          
          {nextToken && !loading && (
             <div className="py-4 text-center">
                <Button variant="outline" onClick={() => fetchFiles(nextToken)}>Load More</Button>
             </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaLibraryModal;